import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Reusable clipboard hook with textarea fallback for non-HTTPS contexts.
 * Returns a `copied` flag that auto-resets after `resetMs`.
 */
export function useCopyToClipboard(text: string, resetMs = 2000) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const copy = useCallback(() => {
    const doCopy = async () => {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), resetMs);
    };
    doCopy();
  }, [text, resetMs]);

  return { copied, copy };
}
